import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { projectsAPI } from '../services/api';
import ProjectList from '../components/Projects/ProjectList';
import VersionHistory from '../components/Projects/VersionHistory';
import EnhancedFileViewer from '../components/Projects/EnhancedFileViewer'; // Changed from FileViewer
import RefactorProject from '../components/Projects/RefactorProject';
import Loader from '../components/UI/Loader';

const ProjectManager = () => {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('files');
  const [error, setError] = useState(null);
  const [showRefactor, setShowRefactor] = useState(false);
  const [refactorSuccess, setRefactorSuccess] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (location.state?.selectedProject) {
      setSelectedProject(location.state.selectedProject);
      loadProjectDetails(location.state.selectedProject.id);
    } else if (location.state?.selectedProjectId) {
      const project = projects.find(p => p.id === location.state.selectedProjectId);
      if (project) {
        setSelectedProject(project);
        loadProjectDetails(project.id);
      }
    }
  }, [location.state, projects]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const projectsData = await projectsAPI.getAll();
      console.log('Loaded projects:', projectsData);
      setProjects(projectsData || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async (projectId) => {
    try {
      setError(null);
      const details = await projectsAPI.getById(projectId);
      console.log('Project details:', details);
      setProjectDetails(details);
      setRefactorSuccess(null);
    } catch (error) {
      console.error('Error loading project details:', error);
      setError('Failed to load project details');
      setProjectDetails(null);
    }
  };

  const handleDownload = async (projectId, projectName = 'project') => {
    try {
      const response = await projectsAPI.download(projectId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${projectName.replace(/ /g, '_')}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download project');
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setActiveTab('files');
    setError(null);
    setShowRefactor(false);
    setRefactorSuccess(null);
    loadProjectDetails(project.id);
  };

  const handleProjectDelete = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await projectsAPI.delete(projectId);
        setProjects(projects.filter(p => p.id !== projectId));
        if (selectedProject?.id === projectId) {
          setSelectedProject(null);
          setProjectDetails(null);
        }
      } catch (error) {
        console.error('Error deleting project:', error);
        setError('Failed to delete project');
      }
    }
  };

  const handleRefactorComplete = (result) => {
    setRefactorSuccess({
      message: 'Project refactored successfully!',
      versionNumber: result.version_number,
      filesCount: result.files_count
    });
    setShowRefactor(false);

    if (selectedProject) {
      loadProjectDetails(selectedProject.id);
    }
  };

  const handleStartRefactor = () => {
    setShowRefactor(true);
    setRefactorSuccess(null);
  };

  const handleCancelRefactor = () => {
    setShowRefactor(false);
  };

  // 🌟 NEW: Handle file save
  const handleSaveFileChanges = async (fileChanges) => {
    if (!selectedProject?.id) {
      setError('No project selected');
      return;
    }

    try {
      setSavingChanges(true);
      setError(null);

      console.log('Saving file changes for project:', selectedProject.id, fileChanges);

      const response = await projectsAPI.updateProjectFiles(
        selectedProject.id,
        fileChanges
      );

      console.log('Files updated successfully:', response);

      // Update local project details
      if (projectDetails?.current_files) {
        const updatedFiles = projectDetails.current_files.map(file => {
          const change = fileChanges.find(c => c.name === file.name);
          return change ? { ...file, content: change.content } : file;
        });

        setProjectDetails(prev => ({
          ...prev,
          current_files: updatedFiles
        }));
      }

      // Reload versions to show new version
      await loadProjectDetails(selectedProject.id);

      setRefactorSuccess({
        message: `Changes saved successfully! New version v${response.version_number} created.`,
        versionNumber: response.version_number
      });

    } catch (error) {
      console.error('Failed to save file changes:', error);
      setError('Failed to save changes: ' + error.message);
    } finally {
      setSavingChanges(false);
    }
  };

  if (loading) {
    return <Loader text="Loading projects..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Project Manager</h1>
        <p className="text-gray-600 mt-2">
          Manage and explore your AI-generated projects
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {/* Success Message */}
      {refactorSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{refactorSuccess.message}</span>
          </div>
          {refactorSuccess.versionNumber && (
            <div className="text-sm mt-1 ml-7">
              Version v{refactorSuccess.versionNumber} created
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Project List Sidebar */}
        <div className="xl:col-span-1">
          <ProjectList
            projects={projects}
            selectedProject={selectedProject}
            onProjectSelect={handleProjectSelect}
            onProjectDelete={handleProjectDelete}
          />
        </div>

        {/* Project Details */}
        <div className="xl:col-span-3">
          {selectedProject ? (
            <div className="space-y-6">
              {/* Refactor Form */}
              {showRefactor && (
                <RefactorProject
                  project={selectedProject}
                  onRefactorComplete={handleRefactorComplete}
                  onCancel={handleCancelRefactor}
                />
              )}

              {/* Project Details Card */}
              {!showRefactor && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  {/* Project Header */}
                  <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {selectedProject.name}
                        </h2>
                        <p className="text-gray-600 mt-1">
                          {selectedProject.description}
                        </p>
                        <div className="flex items-center mt-2 space-x-4 text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {selectedProject.framework}
                          </span>
                          <span>Versions: {projectDetails?.versions?.length || 0}</span>
                          <span>•</span>
                          <span>Created: {new Date(selectedProject.created_at).toLocaleDateString()}</span>
                          {savingChanges && (
                            <span className="text-yellow-600 animate-pulse">
                              <svg className="inline w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Saving changes...
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDownload(selectedProject.id, selectedProject.name)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm"
                          title="Download project as ZIP"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download ZIP
                        </button>
                        <button
                          onClick={handleStartRefactor}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all shadow-lg"
                          title="Refactor this project with AI"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Refactor with AI
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-6">
                      {['files', 'versions'].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                            activeTab === tab
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {tab === 'files' ? (
                            <span className="flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Files
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Version History
                            </span>
                          )}
                        </button>
                      ))}
                    </nav>
                  </div>

                  {/* Tab Content */}
                  <div className="p-6">
                    {activeTab === 'files' && projectDetails?.current_files ? (
                      <EnhancedFileViewer
                        files={projectDetails.current_files}
                        isEditable={true} // Allow editing in project manager
                        onSaveChanges={handleSaveFileChanges}
                      />
                    ) : activeTab === 'files' && !projectDetails?.current_files ? (
                      <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-200">
                        <div className="text-4xl mb-4">📄</div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">No Files Available</h4>
                        <p className="text-gray-600">This project doesn't have any files yet.</p>
                      </div>
                    ) : null}

                    {activeTab === 'versions' && projectDetails && (
                      <VersionHistory
                        versions={projectDetails.versions || []}
                        projectId={selectedProject.id}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="text-6xl mb-4">📁</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {projects.length === 0 ? 'No Projects Yet' : 'Select a Project'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {projects.length === 0
                    ? 'Get started by generating your first AI project!'
                    : 'Choose a project from the sidebar to view and edit its files'
                  }
                </p>
                {projects.length === 0 && (
                  <a
                    href="/generate"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Your First Project
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectManager;